import { useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import SettingsIcon from '@mui/icons-material/Settings'
import get from 'lodash/get'
import { useTeam } from '../../../contexts/TeamContext'
import { useProjects } from '../../../contexts/ProjectsContext'
import { useServices } from '../../../contexts/ServicesContext'
import { useDeployments } from '../../../contexts/DeploymentsContext'
import { useNodes } from '../../../contexts/NodesContext'
import useNodeSelection from '../../../shared/hooks/useNodeSelection'
import LoadingState from '../../../shared/components/LoadingState'
import ErrorState from '../../../shared/components/ErrorState'
import NodeChipFilter from '../../../shared/components/NodeChipFilter'
import ServicesList from '../../../shared/components/ServicesList'
import Page from '../../../shared/components/layouts/Page'
import PageHeader from '../../../shared/components/layouts/PageHeader'
import DeploymentStatusChip from '../../../shared/components/DeploymentStatusChip'
import ProjectPendingChangesChip from '../../../shared/components/ProjectPendingChangesChip'

function ProjectIndexPage() {
  const { team } = useTeam()
  const { projects, projectsById, loading: projectsLoading, error: projectsError } = useProjects()
  const { servicesByProjectId, loading: servicesLoading } = useServices()
  const { deploymentsByDeployableId, loading: deploymentsLoading } = useDeployments()
  const { nodes, loading: nodesLoading } = useNodes()

  const { activeNodeId, handleSelectNode } = useNodeSelection('projects.selectedNodeId', nodes)

  const loading = projectsLoading || servicesLoading || deploymentsLoading || nodesLoading

  const filteredProjects = useMemo(() => {
    if (!activeNodeId) return projects
    return projects.filter((p) => p.nodeIds?.includes(activeNodeId))
  }, [projects, activeNodeId])

  const pendingCountByNodeId = useMemo(() => {
    const counts = {}
    projects.forEach((project) => {
      if (project.pendingChanges) {
        project.nodeIds?.forEach((nodeId) => {
          counts[nodeId] = (counts[nodeId] || 0) + 1
        })
      }
    })
    return counts
  }, [projects])

  const sortedFilteredProjectIds = useMemo(() => {
    return filteredProjects
      .map((project) => project.id)
      .sort((a, b) => {
        const projectA = projectsById[a]
        const projectB = projectsById[b]
        return (projectA?.name || '').localeCompare(projectB?.name || '')
      })
  }, [filteredProjects, projectsById])

  const renderProjectList = (projectIds) => {
    if (projectIds.length === 0) {
      return (
        <Typography color="text.secondary">
          <FormattedMessage id="dockerProjects.empty" />
        </Typography>
      )
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {projectIds.map((projectId) => {
          const project = projectsById[projectId]
          const projectServices = get(servicesByProjectId, projectId, [])

          return (
            <Box key={projectId} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Project Header */}
              <Box sx={{ bgcolor: 'transparent', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Link
                      component={RouterLink}
                      to={`/teams/${team.slug}/docker/projects/${project?.slug}`}
                    >
                      <Typography variant="h2" fontWeight="medium">
                        {project?.name || 'No Project'}
                      </Typography>
                    </Link>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {project?.pendingChanges
                        ? <ProjectPendingChangesChip project={project} />
                        : <DeploymentStatusChip deployment={get(deploymentsByDeployableId, project.id)} showName showIcon />}
                    </Box>
                  </Box>
                  <IconButton
                    component={RouterLink}
                    to={`/teams/${team.slug}/docker/projects/${project?.slug}`}
                    size="small"
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                  >
                    <SettingsIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Services */}
              <ServicesList services={projectServices} project={project} />
            </Box>
          )
        })}
      </Box>
    )
  }

  if (loading) return <LoadingState />
  if (projectsError) return <ErrorState messageId="dockerServices.error" />

  return (
    <Page>
      <PageHeader
        title={<FormattedMessage id="dockerProjects.title" />}
        entities={{ team }}
        actions={
          <Button
            component={RouterLink}
            to={`/teams/${team.slug}/docker/projects/new`}
            variant="contained"
            color="primary"
          >
            <FormattedMessage id="dockerProjects.createButton" />
          </Button>
        }
      />
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1, overflow: 'hidden', minHeight: 0, width: '100%' }}>
        <Box sx={{ flexShrink: 0, minWidth: { md: 200 }, borderRight: { xs: 0, md: 1 }, borderBottom: { xs: 1, md: 0 }, borderColor: 'divider', overflowY: { xs: 'hidden', md: 'auto' }, py: { xs: 1, md: 3 }, px: 2 }}>
          <NodeChipFilter
            nodes={nodes}
            activeNodeId={activeNodeId}
            onSelect={handleSelectNode}
            pendingCountByNodeId={pendingCountByNodeId}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', py: 3, px: 2 }}>
          {renderProjectList(sortedFilteredProjectIds)}
        </Box>
      </Box>
    </Page>
  )
}

export default ProjectIndexPage
